import React, { memo, useCallback, useMemo } from 'react';
import block from 'bem-cn';
import { shallowEqual, useSelector } from 'react-redux';
import SVG from 'components/SVG';
import InputDate from 'components/InputDate/desktop';

import './AssistantControlPanel.scss';

const b = block('assistant-control-panel');

const SectionBlock = memo(function SectionBlock({ item, label, onClick, active = false }) {
  return (
    <div className={b('section-wrapper')}>
      <div className={b('section', { 'no-icon': !item.icon, active })} onClick={onClick}>
        {item.icon && <SVG className={b('section-icon')} svgProps={{ svg: item.icon }} />}
        <span className={b('section-text')}>{label ?? item.key}</span>
      </div>
    </div>
  );
});

const AssistantControlPanel = ({ model }) => {
  const locale = useSelector(s => s.locale.locale, shallowEqual);
  const assistantLocale = locale?.assistantPanel ?? {};

  const {
    inputState: {
      currentSections,
      currentPeriods,
      currentDate,
      handleOpenDate,
      handleSelectFromDate,
      handleSelectToDate,
      currentSubsections,
      handleSelectCurrentPeriod,
      handleSelectCurrentSection,
      handleSelectCurrentSubsection,
    },
    outputState: { isSectionSelected, isPeriodSelected, isSubsectionSelected, fullContext },
  } = model;

  const renderList = useCallback(
    (items, onSelect) =>
      items.map(item => {
        const isDateSelector = !!item?.isSelector;
        const isActive = isDateSelector && currentDate?.active;
        return (
          <SectionBlock
            key={item.key}
            item={item}
            active={isActive}
            label={assistantLocale?.[item?.key]}
            onClick={() => (isDateSelector ? handleOpenDate() : onSelect(item?.value))}
          />
        );
      }),
    [assistantLocale, currentDate],
  );

  const getSections = useCallback(() => {
    switch (true) {
      case !isSectionSelected:
        const renderedSections = renderList(currentSections, handleSelectCurrentSection);
        return (
          <div className={b('sections')}>
            <div className={b('sections-wrapper')}>{renderedSections.slice(0, 4)}</div>
            <div className={b('sections-wrapper')}>{renderedSections.slice(4)}</div>
          </div>
        );
      case !isPeriodSelected:
        return (
          <div className={b('sections')}>
            <div className={b('sections-wrapper')}>{renderList(currentPeriods, handleSelectCurrentPeriod)}</div>
            {currentDate?.active && (
              <div className={b('sections-wrapper')}>
                <div className={b('input-date')}>
                  <InputDate color="gray" selected={currentDate?.from} onChange={handleSelectFromDate} />
                </div>
                <div className={b('input-date')}>
                  <InputDate color="gray" selected={currentDate?.to} onChange={handleSelectToDate} />
                </div>
              </div>
            )}
          </div>
        );
      case !isSubsectionSelected:
        return (
          <div className={b('sections', { type: 'line' })}>
            {renderList(currentSubsections, handleSelectCurrentSubsection)}
          </div>
        );

      default:
        return null;
    }
  }, [
    isSectionSelected,
    isPeriodSelected,
    isSubsectionSelected,
    currentSections,
    currentPeriods,
    currentSubsections,
    currentDate,
  ]);

  const FullContextElement = useMemo(
    () => <div className={b('sections', { type: 'context' })}>{renderList(fullContext, () => null)}</div>,
    [fullContext],
  );

  return (
    <div className={b()}>
      <h1 className={b('title')}>{assistantLocale?.title}</h1>
      {fullContext?.length > 0 && FullContextElement}
      {getSections()}
    </div>
  );
};

export default AssistantControlPanel;
